import { injectable } from 'inversify';
import isEqual from 'lodash/isEqual';

import { ValidationService } from '@vk-hr-tek/core/validation';

import {
  BranchTaskDto,
  CreateEventTypeDto,
  FinishTaskDto,
  ParallelGroupDto,
  StartTaskDto,
  SystemTaskDto,
  UserTaskDto,
} from '../../dto';

const nodeDtoByType: Record<string, new () => object> = {
  StartEvent: StartTaskDto,
  UserTask: UserTaskDto,
  BranchTask: BranchTaskDto,
  ParallelGroup: ParallelGroupDto,
  SystemTask: SystemTaskDto,
  FinishEvent: FinishTaskDto,
};

type ValidationCache = Map<string, { value: unknown; errors: object }>;

const ROOT_CACHE_KEY = 'root';

@injectable()
export class EventTypesFormValidationService {
  constructor(private validator: ValidationService) {}

  createValidator() {
    const cache: ValidationCache = new Map();

    return (values: CreateEventTypeDto) => this.validateForm(values, cache);
  }

  private async validateForm(
    values: CreateEventTypeDto,
    cache: ValidationCache,
  ): Promise<Record<string, unknown>> {
    const { nodes: formNodes = {}, ...rootFields } = values;

    const rootCached = cache.get(ROOT_CACHE_KEY);
    const rootErrors =
      rootCached && isEqual(rootCached.value, rootFields)
        ? rootCached.errors
        : await this.validator.validate(values, CreateEventTypeDto);

    cache.set(ROOT_CACHE_KEY, { value: rootFields, errors: rootErrors });

    const nodes: Record<string, unknown> = {};

    await Promise.all(
      Object.entries(formNodes).map(async ([id, node]) => {
        const cached = cache.get(id);
        if (cached && isEqual(cached.value, node)) {
          if (Object.keys(cached.errors).length) {
            nodes[id] = cached.errors;
          }
          return;
        }

        const dto = nodeDtoByType[node.type];
        if (!dto) {
          return;
        }

        const errors = await this.validator.validate(node, dto);
        cache.set(id, { value: node, errors });
        if (Object.keys(errors).length) {
          nodes[id] = errors;
        }
      }),
    );

    return {
      ...rootErrors,
      ...(Object.keys(nodes).length ? { nodes } : {}),
    };
  }
}
